"use client"

import { useMemo } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer, type ChartConfig } from "@/components/ui/chart"
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts"
import { Skeleton } from "@/components/ui/skeleton"
import type { ProductDailyPoint } from "./use-product-daily-series"
import { formatCurrency } from "./utils"
import { useTranslations } from "next-intl"


const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
]

/** Orders key suffix appended to product name in pivoted data */
const ORDERS_SUFFIX = "::orders"

interface ProductSalesChartProps {
  series: ProductDailyPoint[]
  isLoading: boolean
  productCount: number
}

export function ProductSalesChart({ series, isLoading, productCount }: ProductSalesChartProps) {
  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardProductSalesChart")

  const { chartData, productNames, chartConfig } = useMemo(() => {
    if (series.length === 0) {
      return { chartData: [], productNames: [], chartConfig: {} as ChartConfig }
    }

    // Collect unique products preserving order of first appearance
    const productMap = new Map<number, string>()
    for (const point of series) {
      if (!productMap.has(point.product_id)) {
        productMap.set(point.product_id, point.product_title)
      }
    }

    const names = Array.from(productMap.values())

    // Pivot: { date, [product_title]: revenue, [product_title::orders]: orders }
    const dateMap = new Map<string, Record<string, number>>()
    for (const point of series) {
      let row = dateMap.get(point.date)
      if (!row) {
        row = { date: point.date as unknown as number } as unknown as Record<string, number>
        dateMap.set(point.date, row)
      }
      row[point.product_title] = (row[point.product_title] ?? 0) + point.revenue
      row[point.product_title + ORDERS_SUFFIX] =
        (row[point.product_title + ORDERS_SUFFIX] ?? 0) + point.orders
    }

    const data = Array.from(dateMap.values()).sort(
      (a, b) => String(a.date).localeCompare(String(b.date)),
    )

    const config: ChartConfig = {}
    names.forEach((name, i) => {
      config[name] = {
        label: name,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }
    })

    return { chartData: data, productNames: names, chartConfig: config }
  }, [series])

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><span key={tUi("productSales")}>{tUi("productSales")}</span></CardTitle>
          <CardDescription><span key={tUi("loadingProductData")}>{tUi("loadingProductData")}</span></CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[450px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (chartData.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle><span key={tUi("productSales")}>{tUi("productSales")}</span></CardTitle>
        <CardDescription>
          <span><span key={tUi("dailyRevenueFor")}>{tUi("dailyRevenueFor")}</span></span> {productCount} <span><span key={tUi("selectedProduct")}>{tUi("selectedProduct")}</span></span><span>{productCount !== 1 ? "s" : ""}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="aspect-auto h-[450px]">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis tickFormatter={(value: number) => formatCurrency(value)} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                return (
                  <div className="rounded-lg border bg-background p-2.5 shadow-xl text-xs">
                    <div className="mb-1.5 font-medium">{label}</div>
                    <div className="grid gap-1">
                      {payload.map((entry) => {
                        const name = String(entry.dataKey)
                        const ordersKey = name + ORDERS_SUFFIX
                        const row = entry.payload as Record<string, number>
                        const orders = row[ordersKey] ?? 0
                        return (
                          <div key={name} className="flex items-center gap-2">
                            <div
                              className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="flex-1 text-muted-foreground">{name}</span>
                            <span className="font-mono font-medium tabular-nums">
                              {formatCurrency(entry.value as number)}
                            </span>
                            <span className="text-muted-foreground">
                              ({orders} <span key={orders === 1 ? tUi("order") : tUi("orders")}>{orders === 1 ? tUi("order") : tUi("orders")}</span>)
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              }}
            />
            <Legend />
            {productNames.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
